import {
  addPortalRequestPhoto,
  createPortalRequest,
  getPortalDocumentUrl,
  useListPortalRequests,
  type PortalRequestOut,
  type WorkOrderCategory,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';

import { PhoneLink } from '../../components/ContactLinks';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime } from '../../lib/format';
import { CATEGORY, optionsOf, STATUS } from '../maintenance/labels';
import { NotLinked } from './NotLinked';
import { usePortalHome } from './usePortalHome';

const CLOSED = ['completed', 'canceled'];

function RequestCard({ request }: { request: PortalRequestOut }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function addPhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await addPortalRequestPhoto(request.id, { file });
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function view(id: number) {
    setError(null);
    try {
      const link = okData(await getPortalDocumentUrl(id));
      if (link) window.location.assign(link.url);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const status = STATUS[request.status];
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>
            {request.title}
          </Typography>
          {request.priority === 'emergency' && (
            <Chip size="small" color="error" label="Emergency" />
          )}
          <Chip size="small" color={status.color} label={status.label} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {request.place} · {CATEGORY[request.category]} · Sent {formatDateTime(request.created_at)}
        </Typography>
        {request.description && (
          <Typography sx={{ whiteSpace: 'pre-line' }}>{request.description}</Typography>
        )}
        {request.scheduled_on && request.status === 'scheduled' && (
          <Alert severity="info">Scheduled for {formatDate(request.scheduled_on)}.</Alert>
        )}
        {request.completion_notes && <Alert severity="success">{request.completion_notes}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {request.photos.map((photo) => (
            <Button key={photo.id} size="small" onClick={() => void view(photo.id)}>
              {photo.file_name}
            </Button>
          ))}
          {!CLOSED.includes(request.status) && (
            <Button component="label" size="small" variant="outlined" disabled={busy}>
              {busy ? 'Uploading…' : 'Add a photo'}
              <input
                hidden
                type="file"
                aria-label={`Add a photo to ${request.title}`}
                accept=".jpg,.jpeg,.png"
                onChange={(e) => void addPhoto(e)}
              />
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function PortalMaintenancePage() {
  const { home, notLinked } = usePortalHome();
  const requests = okItems(useListPortalRequests({ query: { enabled: !!home } }).data);
  const [asking, setAsking] = useState(false);

  if (notLinked) return <NotLinked />;
  if (!home) return null;
  const current = home.leases.filter((lease) => lease.status === 'active');
  const fields: Field[] = [
    ...(current.length > 1
      ? [
          {
            name: 'lease_id',
            label: 'Which Home',
            type: 'select' as const,
            required: true,
            options: current.map((l) => ({ value: String(l.id), label: l.label })),
          },
        ]
      : []),
    { name: 'title', label: "What's Wrong?", required: true },
    {
      name: 'category',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'general',
      options: optionsOf(CATEGORY),
    },
    { name: 'description', label: 'Details', type: 'multiline' },
    { name: 'emergency', label: 'This Is an Emergency', type: 'checkbox' },
    { name: 'permission_to_enter', label: 'OK to Enter if Nobody Is Home', type: 'checkbox' },
    {
      name: 'entry_notes',
      label: 'Anything to Know Before Entering?',
      helperText: 'Pets, best times',
    },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Maintenance
        </Typography>
        {current.length > 0 && (
          <Button variant="contained" onClick={() => setAsking(true)}>
            New request
          </Button>
        )}
      </Stack>
      <Alert severity="warning">
        If there&apos;s fire, a gas smell or danger to anyone, leave and call 911 first.
        {home.business.phone && (
          <>
            {' '}
            For an urgent repair, also call {home.business.name} at{' '}
            <PhoneLink phone={home.business.phone} />.
          </>
        )}
      </Alert>
      {requests.length === 0 ? (
        <Typography color="text.secondary">You haven&apos;t sent any requests yet.</Typography>
      ) : (
        requests.map((request) => <RequestCard key={request.id} request={request} />)
      )}
      {asking && (
        <FormDialog
          title="New Maintenance Request"
          intro="You can add photos after you send this."
          fields={fields}
          initial={{
            lease_id: '',
            title: '',
            category: 'general',
            description: '',
            emergency: false,
            permission_to_enter: false,
            entry_notes: '',
          }}
          submitLabel="Send request"
          onSubmit={(values) =>
            createPortalRequest({
              lease_id: current.length > 1 ? Number(required(values, 'lease_id')) : current[0].id,
              title: required(values, 'title'),
              category: required(values, 'category') as WorkOrderCategory,
              description: optional(values, 'description'),
              emergency: Boolean(values.emergency),
              permission_to_enter: Boolean(values.permission_to_enter),
              entry_notes: optional(values, 'entry_notes'),
            })
          }
          onClose={() => setAsking(false)}
        />
      )}
    </Stack>
  );
}
